import { MouseEvent } from 'react';

import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';

import { Box, Radio, SxProps, Theme, Typography } from '@mui/material';

interface Answer {
  key: string;
  value: string;
  isSelected: boolean;
}

export type QuestionRowProps = Readonly<{
  title: string;
  description: string;
  expanded: boolean;
  toggleExpand: () => void;
  answers: Answer[];
  onAnswerChange?: (key: string) => void;
  idPrefix?: string;
  isFinalQuestion?: boolean;
  showSeparation?: boolean;
}>;

export const QuestionRow = ({
  title,
  description,
  expanded,
  toggleExpand,
  answers,
  onAnswerChange,
  idPrefix,
  isFinalQuestion,
  showSeparation = false,
}: QuestionRowProps) => {
  const idFor = (suffix: string) =>
    idPrefix ? `questionnaire-${suffix}-${idPrefix}` : `questionnaire-${suffix}`;

  const handleAnswerClick = (e: MouseEvent<HTMLElement>, answer: Answer) => {
    e.stopPropagation();

    if (!expanded) {
      // only allow answering when the item is expanded
      toggleExpand();

      return;
    }

    onAnswerChange?.(answer.key);
  };

  return (
    <Box
      id={idFor('row')}
      onClick={toggleExpand}
      sx={containerSx(expanded, !!isFinalQuestion, !!showSeparation)}
    >
      <Box sx={titleOnlySx(expanded)}>
        <Typography
          variant="text-sm-medium"
          component="div"
          color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
        >
          {title}
        </Typography>
        <AnimatedCollapse isOpen={expanded} direction="vertical">
          <Typography
            variant="text-sm-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {description}
          </Typography>
        </AnimatedCollapse>
      </Box>

      <Box sx={answerContainerSx}>
        {answers.map((answer) => (
          <Box
            key={answer.key}
            sx={answerItemSx(expanded, answers.length)}
            onClick={(e) => handleAnswerClick(e, answer)}
          >
            <Radio
              id={idFor(`answer-${answer.key}`)}
              sx={radioSx}
              checked={answer.isSelected}
              readOnly={!onAnswerChange}
              inputProps={{ 'aria-readonly': !onAnswerChange }}
              onClick={(e) => handleAnswerClick(e, answer)}
              disableRipple={!expanded || !onAnswerChange}
            />
            <AnimatedCollapse isOpen={expanded} direction="vertical">
              <Typography
                variant="text-xs-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
              >
                {answer.value}
              </Typography>
            </AnimatedCollapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const containerSx =
  (expanded: boolean, isFinalQuestion: boolean, showSeparation: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    cursor: 'pointer',
    borderTop: `1px solid ${backrs.colors[expanded ? 'Brand/600' : 'Colors/Border/border-primary']}`,
    borderBottomLeftRadius: isFinalQuestion ? backrs.radius['radius-xl'] : 0,
    borderBottomRightRadius: isFinalQuestion ? backrs.radius['radius-xl'] : 0,
    backgroundColor: expanded ? backrs.colors['Brand/25'] : 'transparent',
    borderBottom: showSeparation
      ? `2px solid ${backrs.colors['Beige/950']}`
      : `1px solid ${backrs.colors[expanded ? 'Brand/600' : 'Colors/Border/border-primary']}`,
  });

const titleOnlySx =
  (expanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '25%',
    height: '100%',

    py: expanded ? backrs.spacing['spacing-6xl'] : backrs.spacing['spacing-3xl'],
    px: {
      xs: backrs.spacing['spacing-xl'],
      md: backrs.spacing['spacing-3xl'],
    },

    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${backrs.colors[expanded ? 'Colors/Base/Transparent' : 'Colors/Border/border-primary']}`,

    transition: 'all 0.3s ease-in-out',
  });

const answerContainerSx: SxProps<Theme> = {
  width: '75%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  transition: 'all 0.3s ease-in-out',
};

const answerItemSx =
  (expanded: boolean, numAnswers: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `calc(100% / ${numAnswers})`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    px: backrs.spacing['spacing-3xl'],
    gap: expanded ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-none'],
    transition: 'all 0.3s ease-in-out',

    pt: expanded ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-3xl'],
    pb: expanded ? backrs.spacing['spacing-4xl'] : backrs.spacing['spacing-3xl'],
  });

const radioSx: SxProps<Theme> = {
  margin: 0,
  padding: 0,
};
